import { ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { get } from 'lodash';
import {
  AuctionService,
  BookProviderService,
  BookValueSpreadProviderName,
  ConfigurationState,
  InventoryItemReviewedDto,
  InventoryService,
  MissedOfferAlternativeRecommendationVehicleDto,
  OrganizationState,
  UserProfileState,
  MatPagination
} from 'remarketing-angular-library';
import {
  DatalistItemComponent,
  ErrorIconComponent,
  MarkReviewedCheckboxComponent,
  SlideOutComponent,
  SpinnerComponent,
  TooltipComponent,
  VehicleIconComponent
} from 'remarketing-ui-components-lib';
import { take, takeUntil } from 'rxjs';
import { AnalyticsService, CurrencyFormatService, SaComponent } from 'smartauction-frontend-micro-library';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { checkMobile } from '../../../app/shared/utils/util';
import { CommonModule } from '@angular/common';
import { AddRemoveBidListButtonComponent } from '../add-remove-bid-list-button/add-remove-bid-list-button.component';
import { Router, RouterLink } from '@angular/router';
import { VehicleDetailRoutingService } from '../../../vehicles/services/vehicle-detail-routing/vehicle-detail-routing.service';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { AddRemoveBidListButtonEnum } from '../add-remove-bid-list-button/add-remove-bid-list-button.enum';

@Component({
    selector: 'app-missed-offer-alternative-recommendations',
    templateUrl: './missed-offer-alternative-recommendations.component.html',
    styleUrls: ['./missed-offer-alternative-recommendations.component.scss'],
    imports: [
        CommonModule,
        SlideOutComponent,
        RouterLink,
        DatalistItemComponent,
        VehicleIconComponent,
        TooltipComponent,
        SpinnerComponent,
        ButtonsModule,
        AddRemoveBidListButtonComponent,
        DatalistItemComponent,
        VehicleIconComponent,
        TooltipComponent,
        MarkReviewedCheckboxComponent,
        RouterLink,
        MatPaginatorModule,
        SpinnerComponent,
        ErrorIconComponent
    ]
})
export class MissedOfferAlternativeRecommendationsComponent extends SaComponent implements OnInit, OnDestroy {
  @ViewChild(SlideOutComponent) slideOut: SlideOutComponent;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() title = '';
  @Input() slideOutId = '';
  @Input() bidListVin = '';
  moaVehicleRecommendationData: MissedOfferAlternativeRecommendationVehicleDto[] = [];
  defaultBookProvider: string;
  defaultProviderValue: string;
  buyerOeUuid: string;
  tenantName: string;
  userUuid: string;
  pagination: MatPagination;
  dataSource = new MatTableDataSource<any>([]);
  totalLength = 0;
  pageSize = 6;
  pageIndex = 0;
  startIndex = 0;
  endIndex = 0;
  desktopPagePerView = 6;
  mobilePagePerView = 3;
  recommendationsHaveLoaded = false;
  selectedIndex: number;
  widgetError: boolean;
  addOrRemoveErrorText: boolean;

  missedOfferAlternativeTranslations = {
    recommendedForYouHeader: '',
    distanceLabel: '',
    distanceUnitAbbrev: '',
    similarVehiclesHeader: '',
    noRecommendationsHeader: '',
    locationLabel: '',
    odometerLabel: '',
    currentBidLabel: '',
    openingPriceLabel: '',
    spreadToBookLabel: '',
    buyNowPriceLabel: '',
    markReviewedLabel: '',
    reviewedLabel: '',
    distanceUnit: '',
    notAvailableText: '',
    viewSimilarBtn: '',
    spreadToBookMetaTitle: '',
    spreadToBookTitle: '',
    spreadToBookBody: '',
    labelDeliveryDropdown: '',
    labelCreditLineDropdown: '',
    labelDealershipDropdown: '',
    labelSubmit: '',
    btnCancel: '',
    headerDealer: '',
    addToBidlistSucess: '',
    cannotAddToBidListError: '',
    cannotRemoveFromBidListError: ''
  };

  get isMobile() {
    return checkMobile();
  }

  get getPageSize() {
    return this.isMobile ? this.mobilePagePerView : this.desktopPagePerView;
  }

  get addOrRemoveBidListIconErrorText(): string {
    return this.addOrRemoveErrorText ? this.missedOfferAlternativeTranslations.cannotAddToBidListError : this.missedOfferAlternativeTranslations.cannotRemoveFromBidListError;
  }

  constructor(
    private changeDetector: ChangeDetectorRef,
    private readonly translocoService: TranslocoService,
    private readonly currencyFormatService: CurrencyFormatService,
    private readonly store: Store,
    private readonly bookProviderService: BookProviderService,
    private readonly auctionService: AuctionService,
    private readonly inventoryService: InventoryService,
    private readonly analyticsService: AnalyticsService,
    private readonly vehicleDetailRoutingService: VehicleDetailRoutingService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getTranslations();
    this.setStateValues();
    this.initializeHomepageData();
    if (!this.bidListVin) {
      this.showMoaRecommendations();
    }
  }

  ngOnDestroy(): void {
    // resets overflow property when leaving the bid list while the slideout feature is open
    document.body.style.overflow = '';
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    if (!this.bidListVin) {
      const pageEvent: PageEvent = {
        pageIndex: this.onResizeCalculateIndex(),
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      };
      this.handlePageEvent(pageEvent);
    }
  }

  get spreadToBookTooltip() {
    return {
      metaTitle: this.missedOfferAlternativeTranslations.spreadToBookMetaTitle,
      title: this.missedOfferAlternativeTranslations.spreadToBookTitle,
      body: `${this.missedOfferAlternativeTranslations.spreadToBookBody} ${this.bookProviderService.defaultBookProviderName} ${this.bookProviderService.defaultBookProviderValue}.`
    };
  }

  onResizeCalculateIndex(): number {
    if (this.paginator?.pageIndex) {
      return (this.paginator.pageIndex =
        this.paginator.pageIndex * Math.floor(this.pageSize / this.paginator.pageSize));
    } else {
      return 0;
    }
  }

  initializeHomepageData(): void {
    if (!this.bidListVin) {
      this.pagination = {
        currentPage: 1,
        perPage: this.desktopPagePerView,
        maxPages: 4
      };
    }
  }

  trackByInventoryItemUuid(result: any) {
    return result.inventoryItemUuid;
  }

  analyticsClickEvent(vehicle) {
    this.analyticsService.track('clickEvent', {
      tagName: 'Link',
      allytmln: `${vehicle.inventoryItemUuid} - MOA VDP`,
      linkName: `${vehicle.inventoryItemUuid} - MOA VDP`
    });
  }

  setStateValues(): void {
    this.tenantName = this.store.selectSnapshot(ConfigurationState.configuration).tenantName;
    this.buyerOeUuid = this.store.selectSnapshot(OrganizationState.oeUuid);
    this.userUuid = this.store.selectSnapshot(UserProfileState.userUuid);

    this.defaultBookProvider = this.store.selectSnapshot(state =>
      get(state, 'user.userProfile.userPreferences.defaultBookProvider.providerName', null)
    );

    this.defaultProviderValue = this.store.selectSnapshot(state =>
      get(state, 'user.userProfile.userPreferences.defaultBookProvider.providerValue', null)
    );
  }

  getSpreadToBookValue(vehicleBookValues) {
    if (vehicleBookValues) {
      const bookProviders = vehicleBookValues.filter(bookValue => bookValue.providerName === this.defaultBookProvider);
      const filteredProvider = bookProviders[0];
      const providerValue =
        this.defaultBookProvider.toLocaleLowerCase() === BookValueSpreadProviderName.NADA.toLocaleLowerCase() ||
        this.defaultBookProvider.toLocaleLowerCase() === BookValueSpreadProviderName.BLACK_BOOK.toLocaleLowerCase()
          ? `${this.defaultProviderValue}`.toLocaleLowerCase()
          : null;

      const filteredBookValues = filteredProvider?.values.filter(
        value => value.providerValue.toLocaleLowerCase() === providerValue
      );
      return filteredBookValues && filteredBookValues.length > 0 ? filteredBookValues[0].spreadToBook : undefined;
    }
    return undefined;
  }

  onMarkReviewed(payload: InventoryItemReviewedDto) {
    this.analyticsReviewedClickEvent('Mark');
    this.inventoryService
      .markReviewed(payload)
      .pipe(take(1))
      .subscribe();
  }

  onUnmarkReviewed(payload: InventoryItemReviewedDto) {
    this.analyticsReviewedClickEvent('Unmark');
    this.inventoryService
      .unmarkReviewed(payload)
      .pipe(take(1))
      .subscribe();
  }

  analyticsReviewedClickEvent(action: string) {
    this.analyticsService.track('clickEvent', {
      tagName: 'BUTTON',
      allytmln: `${action} Reviewed`,
      linkName: `${action} Reviewed`
    });
  }

  formatCurrency(currency): string {
    if (currency) {
      return this.currencyFormatService.formatCurrency(currency, false);
    }
  }

  formatSpreadToBookPercentage(vehicleBookValues) {
    return this.getSpreadToBookValue(vehicleBookValues)
      ? ' (' + Math.trunc(this.getSpreadToBookValue(vehicleBookValues) * 100) + '%)'
      : '';
  }

  formatOdometer(odometer): string {
    return odometer
      ? `${odometer.toLocaleString()} ${this.missedOfferAlternativeTranslations.distanceUnit}`
      : this.missedOfferAlternativeTranslations.notAvailableText;
  }

  formatBuyNowAmount(amount): string {
    return amount ? this.formatCurrency(amount) : this.missedOfferAlternativeTranslations.notAvailableText;
  }

  formatDistanceUnitAbbrev(distance): string {
    return distance
      ? `${distance.toLocaleString()} ${this.missedOfferAlternativeTranslations.distanceUnitAbbrev}`
      : this.missedOfferAlternativeTranslations.notAvailableText;
  }

  vdpLinkText(vehicle): string {
    return `${vehicle.modelYear} ${vehicle.make} ${vehicle.model}`;
  }

  getBuyVdpLink(vehicle): string {
    return this.vehicleDetailRoutingService.getVehicleDetailRoute(
      vehicle.auctionItemUuid,
      vehicle.inventoryItemUuid
    );
  }

  showMoaRecommendations() {
    this.recommendationsHaveLoaded = false;
    if (this.bidListVin && this.slideOut) {
      this.slideOut.show();
      this.getSlideoutTranslations();
    }

    this.auctionService
      .getMissedOfferAlternativeVehicleRecommendations(this.userUuid, this.bidListVin)
      .pipe(take(1))
      .subscribe(
        items => {
          this.moaVehicleRecommendationData = items;
          if (!this.bidListVin) {
            this.dataSource.data = this.moaVehicleRecommendationData;
            this.totalLength = this.dataSource.data.length;
            this.dataSource.paginator = this.paginator;
            this.updatePaginatedData();
          }
          this.recommendationsHaveLoaded = true;
          this.changeDetector.detectChanges();
        },
        err => {
          console.error(err);
        }
      );
  }

  handlePageEvent(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedData();
  }

  updatePaginatedData() {
    this.selectedIndex = undefined;
    this.widgetError = false;
    // resetting widget error when pagination changes

    if (this.isMobile) {
      this.startIndex = this.pageIndex * 3;
      this.endIndex = this.startIndex + 3;
    } else {
      this.startIndex = this.pageIndex * 6;
      this.endIndex = this.startIndex + 6;
    }
    this.changeDetector.detectChanges();
    this.moaVehicleRecommendationData = this.dataSource.data.slice(this.startIndex, this.endIndex);
  }

  onErrorMessage(errorType, i) {
    this.widgetError = false;
    this.selectedIndex = i;
    if (errorType) {
      errorType === AddRemoveBidListButtonEnum.ADD
        ? (this.addOrRemoveErrorText = true)
        : (this.addOrRemoveErrorText = false);
      this.widgetError = true;
    }
  }

  getTranslations(): void {
    const keyParams = {
      'vehicles.moaRecommendation': {},
      common: {},
      'search.inventoryItem': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translations => {
        this.missedOfferAlternativeTranslations.distanceUnit = translations[1].distanceUnit;
        this.missedOfferAlternativeTranslations.notAvailableText = translations[1].notAvailable;
        this.missedOfferAlternativeTranslations.recommendedForYouHeader = translations[0].recommendedForYouHeader;
        this.missedOfferAlternativeTranslations.distanceLabel = translations[0].distanceLabel;
        this.missedOfferAlternativeTranslations.distanceUnitAbbrev = translations[1].distanceUnitAbbrev;
        this.missedOfferAlternativeTranslations.btnCancel = translations[1].btnCancel;
        this.missedOfferAlternativeTranslations.labelSubmit = translations[1].labelSubmit;
        this.missedOfferAlternativeTranslations.odometerLabel = translations[0].odometerLabel;
        this.missedOfferAlternativeTranslations.currentBidLabel = translations[0].currentBidLabel;
        this.missedOfferAlternativeTranslations.openingPriceLabel = translations[0].openingPriceLabel;
        this.missedOfferAlternativeTranslations.buyNowPriceLabel = translations[0].buyNowPriceLabel;
        this.missedOfferAlternativeTranslations.labelDealershipDropdown = translations[2].labelDealershipDropdown;
        this.missedOfferAlternativeTranslations.labelCreditLineDropdown = translations[2].labelCreditLineDropdown;
        this.missedOfferAlternativeTranslations.labelDeliveryDropdown = translations[2].labelDeliveryDropdown;
        this.missedOfferAlternativeTranslations.headerDealer = translations[2].headerDealer;
        this.missedOfferAlternativeTranslations.addToBidlistSucess = translations[2].alertMessages.addToBidlistSucess;
        this.missedOfferAlternativeTranslations.noRecommendationsHeader = this.bidListVin
          ? translations[0].noSimilarVehiclesHeader
          : translations[0].noRecommendationsHeader;
        this.missedOfferAlternativeTranslations.cannotAddToBidListError = translations[0].cannotAddToBidListError;
        this.missedOfferAlternativeTranslations.cannotRemoveFromBidListError =
          translations[0].cannotRemoveFromBidListError;
      });
  }

  getSlideoutTranslations(): void {
    const keyParams = {
      'vehicles.moaRecommendation': {},
      common: {},
      'sellModule.details.auctionDetail.tooltipSpreadToBook': {},
      'search.inventoryItem': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(take(1))
      .subscribe(translations => {
        this.missedOfferAlternativeTranslations.locationLabel = translations[0].locationLabel;
        this.missedOfferAlternativeTranslations.odometerLabel = translations[0].odometerLabel;
        this.missedOfferAlternativeTranslations.currentBidLabel = translations[0].currentBidLabel;
        this.missedOfferAlternativeTranslations.openingPriceLabel = translations[0].openingPriceLabel;
        this.missedOfferAlternativeTranslations.spreadToBookLabel = translations[0].spreadToBookLabel;
        this.missedOfferAlternativeTranslations.buyNowPriceLabel = translations[0].buyNowPriceLabel;
        this.missedOfferAlternativeTranslations.markReviewedLabel = translations[0].markReviewedLabel;
        this.missedOfferAlternativeTranslations.reviewedLabel = translations[0].reviewedLabel;
        this.missedOfferAlternativeTranslations.viewSimilarBtn = translations[0].viewSimilarBtn;
        this.missedOfferAlternativeTranslations.similarVehiclesHeader = translations[0].similarVehiclesHeader;
        this.missedOfferAlternativeTranslations.spreadToBookMetaTitle = translations[2].metaTitle;
        this.missedOfferAlternativeTranslations.spreadToBookTitle = translations[2].title;
        this.missedOfferAlternativeTranslations.spreadToBookBody = translations[2].body;
      });
  }
}
